import React, { useEffect, useRef, useState } from 'react';
import { ActiveSelection, FabricObject } from 'fabric';
import { ChevronDown, ChevronUp, Group as GroupIcon, Layers, Ungroup } from 'lucide-react';
import { C, FONT_UI } from '../../constants/design-tokens';
import { getFabricTypeMeta } from '../../canvas/constants/object-meta';
import { useEditorCanvas } from '../../canvas/hooks/use-editor-canvas';
import { useGrouping } from '../../grouping/hooks/use-grouping';
import { usePanels } from '../../shared/hooks/use-panels';
import { isTopToolbarLayout } from '../../inspector/components/settings-panel';

const getIconForObject = (obj: FabricObject) => {
  const Icon = getFabricTypeMeta(obj.type).icon;
  return <Icon size={14} />;
};

export const LayersControl: React.FC = () => {
  const { canvas, layers, selectedObject, focusLayer, toggleLayerInSelection, transaction } = useEditorCanvas();
  const { canGroup, canUngroup, groupSelection, ungroupSelection } = useGrouping(canvas, transaction);
  const { isOpen, toggle } = usePanels();
  const layersOpen = isOpen('layers');

  const [selectedSet, setSelectedSet] = useState<Set<FabricObject>>(new Set());

  const anchorRef = useRef<FabricObject | null>(null);

  useEffect(() => {
    if (!canvas) return;
    const sync = () => setSelectedSet(new Set(canvas.getActiveObjects()));
    canvas.on('selection:created', sync);
    canvas.on('selection:updated', sync);
    canvas.on('selection:cleared', sync);
    sync();
    return () => {
      canvas.off('selection:created', sync);
      canvas.off('selection:updated', sync);
      canvas.off('selection:cleared', sync);
    };
  }, [canvas]);

  // Yield the top-left slot to Settings' top toolbar when a text/shape is
  // selected — both would otherwise pile up in the same area on narrow widths.
  if (isTopToolbarLayout(selectedObject)) return null;

  const count = layers.length;

  const handleLayerClick = (e: React.MouseEvent, target: FabricObject, idx: number) => {
    if (e.shiftKey && anchorRef.current && canvas) {
      const anchorIdx = layers.findIndex(l => l.object === anchorRef.current);
      if (anchorIdx !== -1) {
        const [start, end] = anchorIdx <= idx ? [anchorIdx, idx] : [idx, anchorIdx];
        const range = layers.slice(start, end + 1).map(l => l.object);
        canvas.discardActiveObject();
        canvas.setActiveObject(range.length > 1 ? new ActiveSelection(range, { canvas }) : range[0]);
        canvas.requestRenderAll();
        return;
      }
    }
    if (e.metaKey || e.ctrlKey) toggleLayerInSelection(target);
    else focusLayer(target);
    anchorRef.current = target;
  };

  return (
    <div
      data-panel-id='layers'
      style={{
        position: 'absolute',
        top: 16,
        left: 16,
        zIndex: 15,
        width: 220,
        background: C.panelBg,
        border: `1px solid ${C.panelBorder}`,
        borderRadius: 12,
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        fontFamily: FONT_UI,
        overflow: 'hidden',
        pointerEvents: 'auto'
      }}
    >
      <button
        onClick={() => toggle('layers')}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 12px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: C.textPrimary,
          fontFamily: FONT_UI,
          fontSize: 12,
          fontWeight: 600
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Layers size={14} />
          Capas
          <span style={{ color: C.textMuted, fontWeight: 400 }}>({count})</span>
        </span>
        {layersOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {layersOpen && (
        <>
          <div
            style={{
              display: 'flex',
              gap: 6,
              padding: '8px 10px',
              borderTop: `1px solid ${C.panelBorder}`
            }}
          >
            <button
              onClick={groupSelection}
              disabled={!canGroup}
              title='Agrupar capas seleccionadas'
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '4px 8px',
                fontSize: 11,
                fontFamily: FONT_UI,
                fontWeight: 600,
                color: canGroup ? C.textPrimary : C.textMuted,
                background: 'transparent',
                border: `1px solid ${C.panelBorder}`,
                borderRadius: 6,
                cursor: canGroup ? 'pointer' : 'not-allowed',
                opacity: canGroup ? 1 : 0.5
              }}
            >
              <GroupIcon size={12} /> Agrupar
            </button>
            <button
              onClick={ungroupSelection}
              disabled={!canUngroup}
              title='Desagrupar capa'
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '4px 8px',
                fontSize: 11,
                fontFamily: FONT_UI,
                fontWeight: 600,
                color: canUngroup ? C.textPrimary : C.textMuted,
                background: 'transparent',
                border: `1px solid ${C.panelBorder}`,
                borderRadius: 6,
                cursor: canUngroup ? 'pointer' : 'not-allowed',
                opacity: canUngroup ? 1 : 0.5
              }}
            >
              <Ungroup size={12} /> Desagrupar
            </button>
          </div>
          <div
            style={{
              maxHeight: 280,
              overflowY: 'auto',
              borderTop: `1px solid ${C.panelBorder}`,
              padding: 4
            }}
          >
            {count === 0 ? (
              <div
                style={{
                  padding: '10px 12px',
                  fontSize: 12,
                  color: C.textMuted
                }}
              >
                No hay objetos
              </div>
            ) : (
              layers.map((layer, idx) => {
                const isSelected = selectedSet.has(layer.object);
                return (
                  <button
                    key={layer.stackIndex}
                    onClick={e => handleLayerClick(e, layer.object, idx)}
                    title='Click: una capa · ⌘/Ctrl+Click: sumar/quitar · Shift+Click: rango'
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 10px',
                      background: isSelected ? C.panelSelectedBg : 'transparent',
                      color: isSelected ? C.panelSelectedText : C.textSecondary,
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontFamily: FONT_UI,
                      fontSize: 12,
                      fontWeight: isSelected ? 600 : 400,
                      textAlign: 'left'
                    }}
                  >
                    {getIconForObject(layer.object)}
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {layer.label}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
};
